import { cn } from "@/lib/utils";
import {
  AUTO_PREVIEW_COLOR,
  VEHICLE_COLOR_PALETTE,
  VEHICLE_MODELS,
  vehicleIconDataUri,
  type VehicleColorKey,
  type VehicleModelKey,
} from "@/lib/vehicle-icons";

export interface VehicleIconPickerValue {
  // "photo" = círculo com a foto do veículo no mapa (2026-09-29).
  mapIconStyle: "model" | "photo";
  iconKey: VehicleModelKey;
  iconColorMode: "auto" | "fixed";
  iconColor: VehicleColorKey;
}

interface VehicleIconPickerProps {
  value: VehicleIconPickerValue;
  onChange: (patch: Partial<VehicleIconPickerValue>) => void;
  className?: string;
  // Foto cadastrada do veículo (prévia da opção "Foto do veículo").
  photoUrl?: string;
}

export function VehicleIconPicker({ value, onChange, className, photoUrl }: VehicleIconPickerProps) {
  const previewColor =
    value.iconColorMode === "fixed"
      ? VEHICLE_COLOR_PALETTE.find((c) => c.key === value.iconColor)?.hex ?? AUTO_PREVIEW_COLOR
      : AUTO_PREVIEW_COLOR;

  return (
    <div className={cn("space-y-5", className)}>
      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Como aparece no mapa
        </div>
        <div className="grid grid-cols-2 gap-2 sm:max-w-sm">
          {([
            ["model", "Ícone 3D"],
            ["photo", "Foto do veículo"],
          ] as const).map(([key, label]) => {
            const selected = value.mapIconStyle === key;
            const model = VEHICLE_MODELS.find((m) => m.key === value.iconKey) ?? VEHICLE_MODELS[0];
            return (
              <button
                type="button"
                key={key}
                onClick={() => onChange({ mapIconStyle: key })}
                className={cn(
                  "relative flex flex-col items-center justify-center gap-1.5 rounded-lg border bg-muted/40 p-2.5 transition-all",
                  selected ? "border-primary ring-2 ring-primary/40 bg-primary/5" : "border-border hover:border-primary/40 hover:bg-muted",
                )}
              >
                {key === "model" ? (
                  <img src={vehicleIconDataUri(model, previewColor)} alt="" className="h-12 w-12 object-contain" />
                ) : (
                  <span className="grid h-12 w-12 place-items-center overflow-hidden rounded-full border-[3px] border-emerald-500 bg-slate-900 shadow">
                    {photoUrl ? (
                      <img src={photoUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <img src={vehicleIconDataUri(model, previewColor)} alt="" className="h-9 w-9 object-contain" />
                    )}
                  </span>
                )}
                <span className="text-xs font-medium">{label}</span>
                {selected && (
                  <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {value.mapIconStyle === "photo" && (
          <p className="text-[11px] text-muted-foreground">
            {photoUrl
              ? "No mapa o veículo aparece como um círculo grande com esta foto; a borda muda de cor pelo status e uma seta mostra a direção quando anda."
              : "Este veículo ainda não tem foto — cadastre uma na seção de foto acima. Até lá, o círculo mostra o ícone 3D."}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Tipo do veículo
        </div>
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
          {VEHICLE_MODELS.map((model) => {
            const selected = model.key === value.iconKey;
            return (
              <button
                type="button"
                key={model.key}
                title={model.label}
                aria-label={model.label}
                onClick={() => onChange({ iconKey: model.key })}
                className={cn(
                  "group relative flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border bg-muted/40 p-1.5 transition-all",
                  selected
                    ? "border-primary ring-2 ring-primary/40 bg-primary/5"
                    : "border-border hover:border-primary/40 hover:bg-muted",
                )}
              >
                <img
                  src={vehicleIconDataUri(model, previewColor)}
                  alt={model.label}
                  loading="lazy"
                  className="h-9 w-9 object-contain"
                />
                <span className="line-clamp-1 text-[9px] font-medium text-muted-foreground">
                  {model.label}
                </span>
                {selected && (
                  <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Cor do ícone
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onChange({ iconColorMode: "auto" })}
            className={cn(
              "flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-all",
              value.iconColorMode === "auto"
                ? "border-primary bg-primary/5 text-primary ring-2 ring-primary/30"
                : "border-border text-muted-foreground hover:border-primary/40 hover:bg-muted",
            )}
          >
            <span
              className="h-4 w-4 rounded-full"
              style={{
                background:
                  "conic-gradient(#22c55e 0deg 90deg, #3b82f6 90deg 180deg, #f97316 180deg 270deg, #ef4444 270deg 360deg)",
              }}
            />
            Automática (status)
          </button>
          {VEHICLE_COLOR_PALETTE.map((c) => {
            const active = value.iconColorMode === "fixed" && value.iconColor === c.key;
            return (
              <button
                type="button"
                key={c.key}
                title={c.label}
                aria-label={c.label}
                onClick={() => onChange({ iconColorMode: "fixed", iconColor: c.key })}
                className={cn(
                  "relative h-8 w-8 rounded-full border shadow-sm transition-all",
                  active
                    ? "ring-2 ring-offset-2 ring-primary ring-offset-background scale-110"
                    : "hover:scale-105 border-border",
                )}
                style={{ background: c.hex }}
              />
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {value.iconColorMode === "auto"
            ? "A cor do ícone muda sozinha conforme o status: verde (ligado), azul (parado), vermelho (offline ou bloqueado), laranja (sem GPS)."
            : "Cor fixa — o ícone sempre aparece nessa cor no mapa. O status passa a ser indicado só por pequenos indicadores."}
        </p>
      </div>
    </div>
  );
}
